import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ElementType,
  type KeyboardEvent,
} from 'react';
import { createPortal } from 'react-dom';
import { Loader2, MoreVertical } from 'lucide-react';
import {
  resolveTableActionFixedInlineActions,
  resolveTableActionInlineCandidates,
  resolveTableActionOverflow,
} from './table-action-menu-overflow';

export interface TableActionItem {
  id: string;
  label: string;
  onSelect: () => void;
  placement?: 'fixed-inline' | 'inline-priority' | 'menu';
  variant?: 'default' | 'primary';
  disabled?: boolean;
  isLoading?: boolean;
  loadingLabel?: string;
  icon?: ElementType<{ className?: string | undefined }>;
}

export interface TableActionMenuProps {
  actions: TableActionItem[];
  label?: string;
  containerClassName?: string;
  portal?: boolean;
}

const ACTION_GAP_PX = 8;

function actionDisplayLabel(action: TableActionItem): string {
  return action.isLoading ? (action.loadingLabel ?? action.label) : action.label;
}

export function TableActionMenu({
  actions,
  label = 'Table actions',
  containerClassName,
  portal = false,
}: TableActionMenuProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const menuMeasureRef = useRef<HTMLButtonElement>(null);
  const fixedActionMeasureRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const actionMeasureRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const [open, setOpen] = useState(false);
  const [visibleInlineCount, setVisibleInlineCount] = useState(0);
  const [position, setPosition] = useState({ left: 0, top: 0, maxHeight: 384 });

  const fixedInlineActions = useMemo(
    () => resolveTableActionFixedInlineActions(actions),
    [actions]
  );
  const inlineCandidates = useMemo(() => resolveTableActionInlineCandidates(actions), [actions]);

  const recalculateInlineActions = useCallback(() => {
    const availableWidth = containerRef.current?.getBoundingClientRect().width ?? 0;
    const menuWidth = menuMeasureRef.current?.getBoundingClientRect().width ?? 0;

    if (availableWidth === 0) {
      return;
    }

    const fixedActionWidths = fixedInlineActions.map(
      (_action, index) => fixedActionMeasureRefs.current[index]?.getBoundingClientRect().width ?? 0
    );
    const inlineActionWidths = inlineCandidates.map(
      (_action, index) => actionMeasureRefs.current[index]?.getBoundingClientRect().width ?? 0
    );
    const nonFixedActions = actions.filter((action) => action.placement !== 'fixed-inline');
    const hasAlwaysMenuAction = nonFixedActions.some(
      (action) => action.placement !== 'inline-priority'
    );

    let next = resolveTableActionOverflow({
      availableWidth,
      menuWidth: hasAlwaysMenuAction ? menuWidth : 0,
      fixedActionWidths,
      inlineActionWidths,
      gap: ACTION_GAP_PX,
    });
    if (!hasAlwaysMenuAction && next.visibleInlineCount < inlineCandidates.length) {
      next = resolveTableActionOverflow({
        availableWidth,
        menuWidth,
        fixedActionWidths,
        inlineActionWidths,
        gap: ACTION_GAP_PX,
      });
    }

    setVisibleInlineCount((current) =>
      current === next.visibleInlineCount ? current : next.visibleInlineCount
    );
  }, [actions, fixedInlineActions, inlineCandidates]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(recalculateInlineActions);

    if (typeof ResizeObserver === 'undefined') {
      return () => window.cancelAnimationFrame(frame);
    }

    const observer = new ResizeObserver(() => {
      recalculateInlineActions();
    });

    if (containerRef.current) observer.observe(containerRef.current);
    if (menuMeasureRef.current) observer.observe(menuMeasureRef.current);
    for (const actionRef of fixedActionMeasureRefs.current) {
      if (actionRef) observer.observe(actionRef);
    }
    for (const actionRef of actionMeasureRefs.current) {
      if (actionRef) observer.observe(actionRef);
    }

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [recalculateInlineActions]);

  useEffect(() => {
    if (!open) {
      return;
    }

    function handlePointerDown(event: Event) {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }

      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) {
        return;
      }

      setOpen(false);
    }

    function handleKeyDown(event: globalThis.KeyboardEvent) {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }

    document.addEventListener('pointerdown', handlePointerDown);
    if (portal) document.addEventListener('focusin', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      if (portal) document.removeEventListener('focusin', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, portal]);

  const visibleInlineIds = useMemo(
    () => new Set(inlineCandidates.slice(0, visibleInlineCount).map((action) => action.id)),
    [inlineCandidates, visibleInlineCount]
  );
  const inlineActions = actions.filter((action) => visibleInlineIds.has(action.id));
  const menuActions = actions.filter(
    (action) => action.placement !== 'fixed-inline' && !visibleInlineIds.has(action.id)
  );

  useLayoutEffect(() => {
    if (!open || !portal) return;
    function positionMenu() {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 12;
      const height = Math.min(384, menuRef.current?.scrollHeight ?? 384);
      const placeAbove = below < height && above > below;
      const maxHeight = Math.max(0, Math.min(384, placeAbove ? above : below));
      setPosition({
        left: Math.max(8, Math.min(rect.right - 192, window.innerWidth - 200)),
        top: placeAbove ? Math.max(8, rect.top - Math.min(height, maxHeight) - 4) : rect.bottom + 4,
        maxHeight,
      });
    }
    positionMenu();
    menuRef.current?.querySelector<HTMLButtonElement>('button:not(:disabled)')?.focus();
    window.addEventListener('resize', positionMenu);
    window.addEventListener('scroll', positionMenu, true);
    return () => {
      window.removeEventListener('resize', positionMenu);
      window.removeEventListener('scroll', positionMenu, true);
    };
  }, [open, portal]);

  function selectAction(action: TableActionItem) {
    if (action.disabled || action.isLoading) {
      return;
    }

    action.onSelect();
    setOpen(false);
  }

  function handleTriggerKeyDown(event: KeyboardEvent<HTMLButtonElement>) {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
    }
  }

  function renderActionButton(action: TableActionItem, isMeasure = false) {
    const Icon = action.icon;
    const disabled = action.disabled || action.isLoading;
    const variantClass =
      action.variant === 'primary'
        ? 'border-backoffice-500 bg-backoffice-500 text-white hover:bg-backoffice-600 disabled:text-white/70'
        : 'border-neutral-200 bg-white text-neutral-700 hover:bg-backoffice-50 disabled:text-neutral-400';

    return (
      <button
        key={`${action.id}-${isMeasure ? 'measure' : 'visible'}`}
        type="button"
        disabled={isMeasure ? undefined : disabled}
        className={[
          'focus-ring inline-flex h-10 items-center gap-1.5 whitespace-nowrap rounded-lg border px-3 text-small font-medium disabled:cursor-not-allowed',
          variantClass,
        ].join(' ')}
        onClick={isMeasure ? undefined : () => selectAction(action)}
      >
        {action.isLoading && !isMeasure ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : Icon ? (
          <Icon className="h-4 w-4" aria-hidden="true" />
        ) : null}
        {isMeasure ? action.label : actionDisplayLabel(action)}
      </button>
    );
  }

  const menu = (
    <div
      ref={menuRef}
      role="menu"
      // Match StatusMultiSelect: viewport coordinates let the portal escape scrollable tiles.
      style={portal ? position : undefined}
      className={`w-48 rounded-lg border border-neutral-200 bg-white p-1 shadow-lg ${portal ? 'fixed z-50 max-w-[calc(100vw-1rem)] overflow-y-auto' : 'absolute right-0 top-full z-20 mt-2'}`}
    >
      {menuActions.map((action) => {
        const Icon = action.icon;
        const disabled = action.disabled || action.isLoading;

        return (
          <button
            key={action.id}
            type="button"
            role="menuitem"
            disabled={disabled}
            className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-left text-body font-medium text-neutral-700 hover:bg-backoffice-50 disabled:cursor-not-allowed disabled:text-neutral-400"
            onClick={() => selectAction(action)}
          >
            {action.isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : Icon ? (
              <Icon className="h-4 w-4" aria-hidden="true" />
            ) : null}
            <span>{actionDisplayLabel(action)}</span>
          </button>
        );
      })}
    </div>
  );

  return (
    <div
      ref={containerRef}
      className={['relative flex min-w-[2.5rem] items-center justify-end gap-2', containerClassName]
        .filter(Boolean)
        .join(' ')}
    >
      {inlineActions.map((action) => renderActionButton(action))}
      {fixedInlineActions.map((action) => renderActionButton(action))}

      {menuActions.length > 0 && (
        <>
          <button
            ref={triggerRef}
            type="button"
            className="focus-ring inline-flex h-10 w-10 items-center justify-center rounded-lg border border-neutral-200 bg-white text-neutral-600 hover:bg-backoffice-50"
            aria-label={label}
            aria-haspopup="menu"
            aria-expanded={open}
            onClick={() => setOpen((current) => !current)}
            onKeyDown={handleTriggerKeyDown}
          >
            <MoreVertical className="h-4 w-4" aria-hidden="true" />
          </button>

          {open && (portal ? createPortal(menu, document.body) : menu)}
        </>
      )}

      <div
        aria-hidden="true"
        className="pointer-events-none invisible absolute right-0 top-0 flex whitespace-nowrap"
      >
        <button
          ref={menuMeasureRef}
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-neutral-200"
        >
          <MoreVertical className="h-4 w-4" />
        </button>
        {fixedInlineActions.map((action, index) => (
          <span
            key={action.id}
            ref={(element) => {
              fixedActionMeasureRefs.current[index] = element?.querySelector('button') ?? null;
            }}
            className="ml-2"
          >
            {renderActionButton(action, true)}
          </span>
        ))}
        {inlineCandidates.map((action, index) => (
          <span
            key={action.id}
            ref={(element) => {
              actionMeasureRefs.current[index] = element?.querySelector('button') ?? null;
            }}
            className="ml-2"
          >
            {renderActionButton(action, true)}
          </span>
        ))}
      </div>
    </div>
  );
}
